import { FC, useRef } from "react";
import { Control } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
} from "../../../../../../components";
import { MuiDoubleLabelWithChip } from "../../../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import { MAX_LENGTH_75 } from "../../../../../../helpers/constants";
import { UnitTypesKey } from "../../../../../../helpers/constants/siteCoreKeys";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  CreateDeliverablesFormKey,
  CreateDeliverablesFormProps,
} from "../../../../modelsAndSchemas/Models";
import { createProgramSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { NewDeliverableWrapper } from "./NewDeliverable.style";

const gridSize = { md: 4, sm: 6, xs: 12 };

export interface NewDeliverableProps {
  pageId: string;
  selectedUnit: string;
  control: Control<CreateDeliverablesFormProps>;
  handleUnitChange: (unit: string) => void;
  isEditDeliverable: boolean;
  activityTypes: OptionsDataType[];
}

const NewDeliverable: FC<NewDeliverableProps> = ({
  pageId,
  selectedUnit,
  control,
  handleUnitChange,
  isEditDeliverable,
  activityTypes,
}) => {
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const gridRef = useRef<HTMLDivElement>(null);

  const renderMeasurement = (
    measurementOtherMinuteName: string,
    measurementOther: string,
  ) => {
    return (
      <>
        {selectedUnit === UnitTypesKey.Minutes && (
          <FormController
            control={control}
            name={
              measurementOtherMinuteName as keyof CreateDeliverablesFormProps
            }
          >
            <MuiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.createDeliverableKey,
                createProgramSiteCoreKeys.createDeliverableMinutes,
              )}
              visibleDefaultSelectOption
              options={fetchDropdownValues(
                createProgramSiteCoreKeys.createDeliverableKey,
                createProgramSiteCoreKeys.createDeliverableMinuteList,
              )}
              isMandatory
              id={`${pageId}-${measurementOtherMinuteName}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        )}

        {(selectedUnit === UnitTypesKey.Count ||
          selectedUnit === UnitTypesKey.Days) && (
          <FormController
            control={control}
            name={measurementOther as keyof CreateDeliverablesFormProps}
          >
            <MuiTextField
              label={fetchLabel(
                createProgramSiteCoreKeys.createDeliverableKey,
                selectedUnit === UnitTypesKey.Count
                  ? createProgramSiteCoreKeys.createDeliverableQuantity
                  : createProgramSiteCoreKeys.createDeliverableNumberOfDays,
              )}
              id={`${pageId}-${measurementOther}`}
              type="number"
              placeholder={commonLabels.inputPlaceHolder}
              isMandatory
              inputNumberWithoutDecimal
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        )}
      </>
    );
  };

  return (
    <NewDeliverableWrapper>
      <MuiGrid container className="form-container">
        <MuiGrid size={gridSize} className="">
          <FormController
            control={control}
            name={CreateDeliverablesFormKey.deliverableType}
          >
            <MuiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.createDeliverableKey,
                createProgramSiteCoreKeys.deliverableType,
              )}
              options={fetchDropdownValues(
                createProgramSiteCoreKeys.createDeliverableKey,
                createProgramSiteCoreKeys.deliverableTypeList,
              )}
              isMandatory
              value=""
              visibleDefaultSelectOption
              id={`${pageId}-${CreateDeliverablesFormKey.deliverableType}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize}>
          <MuiBox className="deliverable-name">
            <FormController
              control={control}
              name={CreateDeliverablesFormKey.name}
            >
              <MuiTextField
                label={fetchLabel(
                  createProgramSiteCoreKeys.createDeliverableKey,
                  createProgramSiteCoreKeys.deliverableName,
                )}
                placeholder={commonLabels.inputPlaceHolder}
                id={`${pageId}-${CreateDeliverablesFormKey.name}`}
                isMandatory
                maxLength={MAX_LENGTH_75}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiBox>
        </MuiGrid>

        {isEditDeliverable && (
          <MuiGrid size={gridSize}>
            <MuiDoubleLabelWithChip
              label={fetchLabel(
                createProgramSiteCoreKeys.deliverablesListKey,
                createProgramSiteCoreKeys.deliverablesActivityType,
              )}
              replaceEmptyWithDash={!activityTypes.length}
              value={activityTypes}
              chipPopoverGridRef={gridRef}
              chipPopoverHeaderLabel={fetchLabel(
                createProgramSiteCoreKeys.deliverablesListKey,
                createProgramSiteCoreKeys.selectedActivities,
              )}
              showMoreARIALabel={commonARIALabel.showNMore}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
          </MuiGrid>
        )}

        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={CreateDeliverablesFormKey.units}
          >
            <MuiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.createDeliverableKey,
                createProgramSiteCoreKeys.createDeliverableUnits,
              )}
              options={fetchDropdownValues(
                createProgramSiteCoreKeys.createDeliverableKey,
                createProgramSiteCoreKeys.createDeliverableUnitList,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${CreateDeliverablesFormKey.units}`}
              isMandatory
              placeholder={commonLabels.selectPlaceHolder}
              onChange={(e) => handleUnitChange(e.target.value as string)}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className="quantity-input-field">
          {renderMeasurement(
            CreateDeliverablesFormKey.measurementMinute,
            CreateDeliverablesFormKey.measurementOther,
          )}
        </MuiGrid>
      </MuiGrid>
    </NewDeliverableWrapper>
  );
};

export default NewDeliverable;
